import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { Text, Box, Logo, Level, Button, IconManager, ldslivingLogoPack } from '@deseretbook/react-ui'
import { usePopup } from '@deseretbook/react-utils'
import { withCheckout } from './CheckoutContext'
import { SignupModal } from '../subscriptions'
import { DeseretBook } from '../../dbcom/api'
import { Logger, formatErrorMessage } from '../../dbcom'
import PlatinumPointsDisplay from '../PlatinumPointsValue'

IconManager.register(ldslivingLogoPack)

const logger = new Logger({ context: 'CheckoutSidebarContent' })

/**
 * Additional content for checkout page.
 *
 * @returns {function} Component
 */
const CheckoutSidebarContent = ({ user, updateUser, order }) => {
  const currentDate = new Date().toISOString().slice(0, 10)
  const isPlatinum = user && (user.platinum || !!user.subscriptions
    .filter(({ subscriptionPlan, awaitingCompletion, serviceableUntil, status }) => !awaitingCompletion && (serviceableUntil >= currentDate || status === 'Active') && subscriptionPlan && subscriptionPlan.type === 'SubscriptionPlan::Platinum')
    .length)
  const isPlus = user && !!user.subscriptions
    .filter(({ subscriptionPlan, awaitingCompletion, serviceableUntil, status }) => !awaitingCompletion && (serviceableUntil >= currentDate || status === 'Active') && subscriptionPlan && subscriptionPlan.type === 'SubscriptionPlan::BookshelfPlus')
    .length

  const [hasPlatinumModal] = useState(!isPlatinum)
  const [hasPlusModal, setHasPlusModal] = useState(!isPlus && isPlatinum)
  const popup = usePopup()

  /**
   * Reloads what we know about the user because it's probably changed.
   */
  const reloadUser = async () => {
    if (!user || !user.id) {
      return
    }

    try {
      const userRequest = DeseretBook.user(user).get()
      const walletRequest = DeseretBook.user(user).walletPaymentSource().all({ perPage: 1000 })
      const addressBookRequest = DeseretBook.user(user).addressBook().get()

      const [
        updatedUser,
        { walletPaymentSources },
        addresses,
      ] = await Promise.all([userRequest, walletRequest, addressBookRequest])

      updateUser({
        ...updatedUser,
        walletPaymentSources,
        addresses,
      })
    } catch ({ message, errors }) {
      logger.error(`Unable to reload user: "${formatErrorMessage(message, errors)}"`, { errors, user })
    }
  }

  return (
    <React.Fragment>
      {isPlatinum && (
        <Level>
          <Level.Left>
            <Level.Item>
              <Logo name="platinumCrest" size="medium" />
            </Level.Item>
          </Level.Left>
          <Level.Item>
            <Text>
              You are earning <PlatinumPointsDisplay points={order.earnablePlatinumPoints} /> in
              Platinum Points.
            </Text>
          </Level.Item>
        </Level>
      )}
      {!isPlatinum && (
        <React.Fragment>
          <Box paddingBottom={0.5} justifyContent="center">
            <Box maxWidth={20}>
              <Logo name="platinumRewards" />
            </Box>
          </Box>

          <Text alignment="center" display="block">
            <Text isBold>
              Receive <PlatinumPointsDisplay points={order.earnablePlatinumPoints} /> back in
              points on this purchase
            </Text> and 10% back in every purchase going forward (plus many other perks too).
          </Text>
          <Box paddingTop={1} justifyContent="center">
            <Button color="platinum" type="button" {...popup.triggerProps}>
              Sign Up Now
            </Button>
          </Box>
        </React.Fragment>
      )}
      {hasPlatinumModal && (
        <SignupModal
          popupProps={popup.popupProps}
          user={user}
          onClose={() => {
            popup.close()
            setHasPlusModal(true)
            reloadUser()
          }}
          onSuccess={reloadUser}
          documentUID="checkout-platinum"
          documentType="subscription_signup_v1"
        />
      )}
      {(!isPlus && isPlatinum) && (
        <Box paddingTop={2}>
          <Box paddingBottom={0.5} justifyContent="center">
            <Box maxWidth={6}>
              <Logo name="plus" />
            </Box>
          </Box>

          <Text alignment="center" display="block">
            Unlimited Audiobooks & Talks + over 2,500 eBooks
          </Text>
          <Box paddingTop={1} justifyContent="center">
            <Button color="plus" type="button" {...popup.triggerProps}>
              Sign Up Now
            </Button>
          </Box>
        </Box>
      )}
      {hasPlusModal && (
        <SignupModal
          popupProps={popup.popupProps}
          user={user}
          onClose={() => {
            popup.close()
            reloadUser()
          }}
          onSuccess={reloadUser}
          documentUID="checkout-plus"
          documentType="subscription_signup_v1"
        />
      )}
    </React.Fragment>
  )
}

CheckoutSidebarContent.propTypes = {
  user: PropTypes.shape({
    platinum: PropTypes.bool,
  }),
  order: PropTypes.shape({
    earnablePlatinumPoints: PropTypes.number,
  }),
  updateUser: PropTypes.func,
}

CheckoutSidebarContent.defaultProps = {
  user: {},
  order: {},
  updateUser: () => {},
}

export default withCheckout(CheckoutSidebarContent)
